// data/items/index.js
// Central item registry - combines all item modules and provides lookup API

import { getAtlasPercentBg } from '../atlasManifest.js';
import { ORES } from './ores.js';
import { LOGS } from './logs.js';
import { FISH, RAW_FISH, COOKED_FISH, EMPOWERED_FISH } from './fish.js';
import { BARS } from './bars.js';
import { CROPS } from './crops.js';
import { CROP_SEEDS, TREE_SEEDS } from './seeds.js';
// CRAFTED removed - legacy ranged armor, superseded by RANGED_ARMOR in armor.js
// FURNITURE removed - dead architecting items no longer used
import { MISC } from './misc.js';
import { ARROWHEADS } from './arrowheads.js';
import { ARROW_SHAFTS, ARROWS, BOWS } from './woodworking.js';
import { ESSENCE, GLYPHS } from './glyphs.js';
import { HERB_SEEDS, GRIMY_HERBS, CLEAN_HERBS } from './herbs.js';
import { VIALS, POTIONS } from './potions.js';
import { GEMS } from './gems.js';
import { BANDS, PENDANTS } from './jewelry.js';
import { PET_FOOD } from './petFood.js';
import { PET_MATERIALS } from './petMaterials.js';
import { HIDES } from './hides.js';
import { CLOTH } from './cloth.js';
import { ARMOR } from './armor.js';
import { WEAPONS } from './weapons.js';
import { BONES } from './bones.js';
import { GLYPH_CORES } from './glyphCores.js';
import { BULWARKS } from './bulwarks.js';
import { FOCUSES } from './focuses.js';
import { QUIVERS } from './quivers.js';
import { INSIGHTS, LOCKED_INSIGHTS } from './insights.js';
import { COMBAT_PENDANTS } from './combatPendants.js';
import { ALL_BOSS_GEAR } from './bossGear.js';
import { ALL_SET_ARMOR } from './setArmor.js';

/**
 * Item categories for filtering and organization
 */
export const ITEM_CATEGORIES = {
    ORE: 'ore',
    LOG: 'log',
    FISH_RAW: 'fish_raw',
    FISH_COOKED: 'fish_cooked',
    FISH_EMPOWERED: 'fish_empowered',
    BAR: 'bar',
    CROP: 'crop',
    SEED: 'seed',
    TREE_SEED: 'tree_seed',
    CURRENCY: 'currency',
    MISC: 'misc',
    ARROWHEAD: 'arrowhead',
    ARROW_SHAFT: 'arrow_shaft',
    ARROW: 'arrow',
    BOW: 'bow',
    ICHOR: 'ichor',
    GLYPH: 'glyph',
    HERB_SEED: 'herb_seed',
    HERB_GRIMY: 'herb_grimy',
    HERB_CLEAN: 'herb_clean',
    VIAL: 'vial',
    POTION: 'potion',
    GEM: 'gem',
    BAND: 'band',
    PENDANT: 'pendant',
    PET_FOOD: 'pet_food',
    PET_MATERIAL: 'pet_material',
    HIDE: 'hide',
    CLOTH: 'cloth',
    ARMOR: 'armor',
    WEAPON: 'weapon',
    BONE: 'bone',
    GLYPH_CORE: 'glyph_core',
    BULWARK: 'bulwark',
    FOCUS: 'focus',
    QUIVER: 'quiver',
    INSIGHT: 'insight',
    COMBAT_PENDANT: 'combat_pendant',
    BOSS_GEAR: 'boss_gear',
    SET_ARMOR: 'set_armor',
    // Future categories
    EQUIPMENT: 'equipment',
    CONSUMABLE: 'consumable',
    MONSTER_DROP: 'monster_drop'
};

/**
 * Item types for gameplay mechanics
 */
export const ITEM_TYPES = {
    RESOURCE: 'resource',      // Basic gathered/crafted materials
    EQUIPMENT: 'equipment',    // Wearable gear
    CONSUMABLE: 'consumable',  // Food, potions
    CURRENCY: 'currency'       // Coins, tokens
};

/**
 * Build the item registry from all modules
 * Adds default values and category assignments
 */
function buildRegistry() {
    const registry = new Map();

    // Helper to register items with category
    const registerItems = (items, category, defaultType = ITEM_TYPES.RESOURCE) => {
        for (const item of items) {
            registry.set(item.id, {
                icon: '?',                    // Default placeholder icon
                type: defaultType,            // Default type
                ...item,                      // Item data
                category                      // Category override (takes precedence over item.category)
            });
        }
    };

    // Register all item categories
    registerItems(ORES, ITEM_CATEGORIES.ORE);
    registerItems(LOGS, ITEM_CATEGORIES.LOG);
    registerItems(RAW_FISH, ITEM_CATEGORIES.FISH_RAW);
    registerItems(COOKED_FISH, ITEM_CATEGORIES.FISH_COOKED, ITEM_TYPES.CONSUMABLE);
    registerItems(EMPOWERED_FISH, ITEM_CATEGORIES.FISH_EMPOWERED, ITEM_TYPES.CONSUMABLE);
    registerItems(BARS, ITEM_CATEGORIES.BAR);
    registerItems(CROPS, ITEM_CATEGORIES.CROP);
    registerItems(CROP_SEEDS, ITEM_CATEGORIES.SEED);
    registerItems(TREE_SEEDS, ITEM_CATEGORIES.TREE_SEED);
    // CRAFTED removed - legacy ranged armor (see RANGED_ARMOR in armor.js)
    // FURNITURE removed - dead architecting items
    registerItems(MISC, ITEM_CATEGORIES.MISC);
    registerItems(ARROWHEADS, ITEM_CATEGORIES.ARROWHEAD);
    registerItems(ARROW_SHAFTS, ITEM_CATEGORIES.ARROW_SHAFT);
    registerItems(ARROWS, ITEM_CATEGORIES.ARROW);
    registerItems(BOWS, ITEM_CATEGORIES.BOW, ITEM_TYPES.EQUIPMENT);
    registerItems(ESSENCE, ITEM_CATEGORIES.ICHOR);
    registerItems(GLYPHS, ITEM_CATEGORIES.GLYPH);
    registerItems(HERB_SEEDS, ITEM_CATEGORIES.HERB_SEED);
    registerItems(GRIMY_HERBS, ITEM_CATEGORIES.HERB_GRIMY);
    registerItems(CLEAN_HERBS, ITEM_CATEGORIES.HERB_CLEAN);
    registerItems(VIALS, ITEM_CATEGORIES.VIAL);
    registerItems(POTIONS, ITEM_CATEGORIES.POTION, ITEM_TYPES.CONSUMABLE);
    registerItems(GEMS, ITEM_CATEGORIES.GEM);
    registerItems(BANDS, ITEM_CATEGORIES.BAND, ITEM_TYPES.EQUIPMENT);
    registerItems(PENDANTS, ITEM_CATEGORIES.PENDANT, ITEM_TYPES.EQUIPMENT);
    registerItems(PET_FOOD, ITEM_CATEGORIES.PET_FOOD, ITEM_TYPES.CONSUMABLE);
    registerItems(PET_MATERIALS, ITEM_CATEGORIES.PET_MATERIAL);
    registerItems(HIDES, ITEM_CATEGORIES.HIDE);
    registerItems(CLOTH, ITEM_CATEGORIES.CLOTH);
    registerItems(ARMOR, ITEM_CATEGORIES.ARMOR, ITEM_TYPES.EQUIPMENT);
    registerItems(WEAPONS, ITEM_CATEGORIES.WEAPON, ITEM_TYPES.EQUIPMENT);
    registerItems(BONES, ITEM_CATEGORIES.BONE);
    registerItems(GLYPH_CORES, ITEM_CATEGORIES.GLYPH_CORE);
    registerItems(BULWARKS, ITEM_CATEGORIES.BULWARK, ITEM_TYPES.EQUIPMENT);
    registerItems(FOCUSES, ITEM_CATEGORIES.FOCUS, ITEM_TYPES.EQUIPMENT);
    registerItems(QUIVERS, ITEM_CATEGORIES.QUIVER, ITEM_TYPES.EQUIPMENT);
    registerItems(INSIGHTS, ITEM_CATEGORIES.INSIGHT, ITEM_TYPES.CONSUMABLE);
    registerItems(LOCKED_INSIGHTS, ITEM_CATEGORIES.INSIGHT, ITEM_TYPES.CONSUMABLE);
    registerItems(COMBAT_PENDANTS, ITEM_CATEGORIES.COMBAT_PENDANT, ITEM_TYPES.EQUIPMENT);
    registerItems(ALL_BOSS_GEAR, ITEM_CATEGORIES.BOSS_GEAR, ITEM_TYPES.EQUIPMENT);
    registerItems(ALL_SET_ARMOR, ITEM_CATEGORIES.SET_ARMOR, ITEM_TYPES.EQUIPMENT);

    // Special handling for currency
    const coins = registry.get('coins');
    if (coins) {
        coins.type = ITEM_TYPES.CURRENCY;
        coins.category = ITEM_CATEGORIES.CURRENCY;
    }

    return registry;
}

// Build registry on module load
const itemRegistry = buildRegistry();

/**
 * Get item data by ID
 * @param {string} itemId - The item identifier
 * @returns {object|null} Item data or null if not found
 */
export function getItem(itemId) {
    return itemRegistry.get(itemId) || null;
}

/**
 * Check if item exists in registry
 * @param {string} itemId - The item identifier
 * @returns {boolean}
 */
export function hasItem(itemId) {
    return itemRegistry.has(itemId);
}

/**
 * Get item display name
 * Falls back to formatted itemId if not in registry
 * @param {string} itemId - The item identifier
 * @returns {string} Display name
 */
export function getItemName(itemId) {
    const item = itemRegistry.get(itemId);
    if (item) return item.name;
    // Fallback: convert snake_case to Title Case
    return itemId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Get item icon
 * Returns emoji character for standard items, or HTML for sprite-based items
 * @param {string} itemId - The item identifier
 * @param {number} [quantity] - Optional quantity for quantity-based icon tiers (coins, arrow shafts)
 * @returns {string} Icon character, HTML string for sprites, or '?' if not found
 */
export function getItemIcon(itemId, quantity, options) {
    const item = itemRegistry.get(itemId);
    if (!item) return '?';

    const skipAtlas = options?.skipAtlas || false;

    // Check for quantity-based sprites (e.g., coins show bigger stack at higher quantities)
    if (item.sprites && item.sprites.length > 0) {
        let spriteData = item.sprites[0]; // Default to smallest
        if (quantity !== undefined && quantity !== null) {
            for (let i = item.sprites.length - 1; i >= 0; i--) {
                if (quantity >= item.sprites[i].minQty) {
                    spriteData = item.sprites[i];
                    break;
                }
            }
        }
        return _renderSprite(spriteData, skipAtlas);
    }

    // Check for single sprite-based icon
    if (item.sprite) {
        return _renderSprite(item.sprite, skipAtlas);
    }

    return item.icon || '?';
}

/**
 * Render a sprite object to HTML
 * @param {Object} spriteData - Sprite definition with sheet, x, y, width, height
 * @returns {string} HTML string for the sprite
 */
function _renderSprite(spriteData, skipAtlas = false) {
    const { sheet, width = 32, height = 32 } = spriteData;
    let xOffset = 0;
    let yOffset = 0;

    // Support explicit x/y pixel positions (most reliable for mixed-size sheets)
    if (spriteData.x !== undefined && spriteData.y !== undefined) {
        xOffset = spriteData.x;
        yOffset = spriteData.y;
    }
    // Support grid (row/col) layout
    else if (spriteData.row !== undefined && spriteData.col !== undefined) {
        xOffset = spriteData.col * width;
        yOffset = spriteData.row * height;
    }
    // Support vertical (index) layout
    else if (spriteData.index !== undefined) {
        yOffset = spriteData.index * height;
    }

    // Add size class for large sprites (48px+) so CSS can scale them differently
    const sizeClass = (width >= 48 || height >= 48) ? 'sprite-icon-lg' : '';
    // Add aspect class for landscape art (e.g., fish) so contexts can scale them up
    const aspectClass = spriteData.aspect === 'landscape' ? 'sprite-landscape' : '';

    // Atlas lookup for single images (individual HD files)
    // Uses percentage-based positioning so CSS width/height overrides don't break layout
    const isSingleImage = xOffset === 0 && yOffset === 0;
    if (isSingleImage && !skipAtlas) {
        const atlas = getAtlasPercentBg(sheet);
        if (atlas) {
            return `<span class="sprite-icon ${sizeClass} ${aspectClass}" style="background-image: url('${atlas.atlas}'); background-size: ${atlas.bgSize}; background-position: ${atlas.bgPosition}; width: ${width}px; height: ${height}px;" data-hd-src="${sheet}"></span>`;
        }
    }

    // Fallback: use original file
    const bgSize = isSingleImage ? 'background-size: contain; background-position: center;' : '';
    const bgPos = isSingleImage ? '' : `background-position: -${xOffset}px -${yOffset}px;`;
    return `<span class="sprite-icon ${sizeClass} ${aspectClass}" style="background-image: url('${sheet}'); ${bgPos} width: ${width}px; height: ${height}px; ${bgSize}"></span>`;
}

/**
 * Check if item uses a sprite icon
 * @param {string} itemId - The item identifier
 * @returns {boolean} True if item has sprite-based icon
 */
export function hasSprite(itemId) {
    const item = itemRegistry.get(itemId);
    return !!(item?.sprite || item?.sprites);
}

/**
 * Get item background color
 * Returns transparent for sprite-based items (sprite is the visual)
 * @param {string} itemId - The item identifier
 * @returns {string} CSS color value
 */
export function getItemColor(itemId) {
    const item = itemRegistry.get(itemId);
    // Sprite items don't need a background color - the sprite is the visual
    if (item?.sprite || item?.sprites) return 'transparent';
    return item?.color || '#666666';
}

/**
 * Get item type
 * @param {string} itemId - The item identifier
 * @returns {string} Item type or 'resource' as default
 */
export function getItemType(itemId) {
    const item = itemRegistry.get(itemId);
    return item?.type || ITEM_TYPES.RESOURCE;
}

/**
 * Get item category
 * @param {string} itemId - The item identifier
 * @returns {string} Item category or 'misc' as default
 */
export function getItemCategory(itemId) {
    const item = itemRegistry.get(itemId);
    return item?.category || ITEM_CATEGORIES.MISC;
}

/**
 * Get item sell price
 * Uses item's value property (individual pricing per item)
 * @param {string} itemId - The item identifier
 * @returns {number} Sell price in gold (0 if not sellable)
 */
export function getItemSellPrice(itemId) {
    const item = itemRegistry.get(itemId);
    if (!item) return 0;

    // Currency can't be sold
    if (item.category === ITEM_CATEGORIES.CURRENCY) return 0;

    // Use item's value property (each item has individual pricing)
    if (item.value !== undefined) return item.value;

    // Fallback for items without value defined
    return 10;
}

/**
 * Check if an item can be sold
 * @param {string} itemId - The item identifier
 * @returns {boolean} True if item can be sold
 */
export function canSellItem(itemId) {
    const item = itemRegistry.get(itemId);
    if (!item) return false;
    if (item.category === ITEM_CATEGORIES.CURRENCY) return false;
    if (item.value === 0) return false; // Items with value: 0 can't be sold
    return true;
}

/**
 * Get all items in a category
 * @param {string} category - Item category from ITEM_CATEGORIES
 * @returns {object[]} Array of items in that category
 */
export function getItemsByCategory(category) {
    const items = [];
    for (const item of itemRegistry.values()) {
        if (item.category === category) {
            items.push(item);
        }
    }
    return items;
}

/**
 * Get all items of a type
 * @param {string} type - Item type from ITEM_TYPES
 * @returns {object[]} Array of items of that type
 */
export function getItemsByType(type) {
    const items = [];
    for (const item of itemRegistry.values()) {
        if (item.type === type) {
            items.push(item);
        }
    }
    return items;
}

/**
 * Get all registered items
 * @returns {object[]} Array of all items
 */
export function getAllItems() {
    return Array.from(itemRegistry.values());
}

/**
 * Get total count of registered items
 * @returns {number}
 */
export function getItemCount() {
    return itemRegistry.size;
}

/**
 * Register a new item dynamically (for future use with monster drops, etc.)
 * @param {object} item - Item data with at least { id, name }
 * @param {string} category - Item category
 */
export function registerItem(item, category = ITEM_CATEGORIES.MISC) {
    if (!item.id || !item.name) {
        throw new Error('Item must have id and name');
    }
    itemRegistry.set(item.id, {
        icon: '?',
        type: ITEM_TYPES.RESOURCE,
        color: '#666666',
        category,
        ...item
    });
}

// Export for direct access if needed
export { itemRegistry };

export default {
    ITEM_CATEGORIES,
    ITEM_TYPES,
    getItem,
    hasItem,
    getItemName,
    getItemIcon,
    getItemColor,
    hasSprite,
    getItemType,
    getItemCategory,
    getItemSellPrice,
    canSellItem,
    getItemsByCategory,
    getItemsByType,
    getAllItems,
    getItemCount,
    registerItem
};
